import { useMemo } from 'react';
import useQueryRoles from 'api/auth0/roles/useQueryRoles';
import useMutation from 'api/hooks/useMutation';
import mapAuth0UserRoleData from 'api/auth0/organization-members/map-auth0-user-role-data';
import {
  mutation,
  buildVariables as buildCreateOrganizationInvitationVariables,
} from './auth0-create-organization-invitation-mutation-builder';
import permissions from 'api/Apollo/permissions';
import useTrackWithUser from 'api/hooks/useTrackWithUser';
import { ORGANIZATION_INVITATIONS_QUERY_NAME } from './auth0-organization-invitations-query-builder';

export default function useCreateOrganizationInvitation({ onCompleted, ...options } = {}) {
  const [trackWithUser] = useTrackWithUser('user_invited');
  const { roles, loading: auth0RolesLoading } = useQueryRoles();
  const [executeMutation, response] = useMutation(mutation, {
    context: { permission: permissions.createUsers },
    onCompleted: data => {
      if (onCompleted) {
        onCompleted(getResponseBody({ data }));
      }
    },
    refetchQueries: [ORGANIZATION_INVITATIONS_QUERY_NAME],
    ...options,
  });

  const roleListMap = useMemo(
    () => Object.assign({}, ...roles.map(role => ({ [role.name]: role.id }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth0RolesLoading]
  );

  const createOrganizationInvitation = ({ email, roleId, debuggerAccess = false }) => {
    const { roleIds } = mapAuth0UserRoleData({ roleListMap, roleId, debuggerAccess });
    return executeMutation({
      variables: buildVariables({ email, roleIds }),
    }).then(response => {
      if (!response.errors) {
        trackWithUser({ invitee: email });
      }
      return response;
    });
  };

  return [createOrganizationInvitation, response];
}

function getResponseBody(response) {
  return response.data.auth0_create_organization_invitation;
}

export function buildVariables({ email, roleIds }) {
  return {
    ...buildCreateOrganizationInvitationVariables({ email, roleIds }),
  };
}
